import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from "@angular/router";
import { NotificationService } from "../../shared/components/notification/notification.service";
import { UserPermission } from "../../shared/types/app.type";
import { UserInfo, UserType, UserRight } from "../../shared/types/user-info";
import { AuthenticationService } from "../authentication/authentication.service";

@Injectable({
  providedIn: "root",
})
export class AuthGuard implements CanActivate {
  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private notificationService: NotificationService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    const currentUser = this.authenticationService.currentUserInfo;

    this.validateSession(currentUser, state.url);
    this.validateAccess(currentUser, route.data?.allowed);

    return true;
  }

  /**
   * Validates the user session and redirects to the login page if the session is invalid.
   *
   * @private
   * @param {UserInfo} currentUser - The current user's information.
   * @param {string} url - The URL to return to after login.
   * @returns {boolean} - Returns `false` if the session is invalid.
   */
  private validateSession(currentUser: UserInfo, redirectUrl: string): boolean {
    if (!this.isValidSession(currentUser)) {
      this.router.navigate(["/login"], { queryParams: { redirectUrl } });

      this.notificationService.error("You have been logged out");

      return false;
    }
  }

  /**
   * Validates user access permissions and redirects if access is not granted.
   *
   * @private
   * @param {UserInfo} currentUser - The current user's information.
   * @param {any} requiredPermissions - The required permissions to access a resource.
   * @returns {boolean} - Returns `false` if access is not granted.
   */
  private validateAccess(currentUser: UserInfo, requiredPermissions: any): boolean {
    if (requiredPermissions) {
      const canAccess = this.isUserAllowed(currentUser, requiredPermissions);

      if (!canAccess) {
        if (this.router.url === "/") {
          this.router.navigate(["/dashboard"]);
        }

        this.notificationService.error("You don't have access to this resource");

        return false;
      }
    }
  }

  private isValidSession(currentUser: UserInfo): boolean {
    const isValidSession = !!currentUser?.id;

    if (!isValidSession) {
      localStorage.clear();
    }

    return isValidSession;
  }

  private isUserAllowed(currentUser: UserInfo, allowedTypeRights: UserPermission[]): boolean {
    if (!allowedTypeRights?.length) {
      return false;
    }

    return allowedTypeRights.some(
      (permission) =>
        (permission.types.includes(currentUser.type as UserType) || permission.types.includes("*")) &&
        (permission.rights.includes(currentUser.right as UserRight) || permission.rights.includes("*")),
    );
  }
}
